import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { motion, useInView, useAnimation } from "framer-motion";
import { 
  ChevronRight, 
  Search, 
  Code, 
  Server, 
  Lock, 
  Database, 
  ArrowRight,
  Copy,
  Globe,
  BarChart3,
  Terminal
} from "lucide-react";

import { NavBar } from "@/components/navbar";
import {Footer} from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Helmet } from "react-helmet-async";

const APIPage = () => {
  const { t } = useTranslation("common");
  
  // Animation controls for scroll animations
  const controls = useAnimation();
  const featuresRef = useRef(null);
  const featuresInView = useInView(featuresRef, { once: true });
  
  useEffect(() => {
    document.title = "API & Integrations | Verdea";
    
    if (featuresInView) {
      controls.start("visible");
    }
  }, [controls, featuresInView]);

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  const apiFeatures = [
    {
      icon: Code,
      title: "RESTful API",
      description: "Integrate Verdea with your existing systems using our RESTful API. Automate data import, report generation, and more."
    },
    {
      icon: Server,
      title: "Secure Authentication",
      description: "Protect your data with industry-standard authentication protocols. Our API supports OAuth 2.0 and API key authentication."
    },
    {
      icon: Lock,
      title: "Data Encryption",
      description: "All data transmitted through our API is encrypted using TLS 1.3. We also offer data encryption at rest for sensitive information."
    },
    {
      icon: Database,
      title: "Webhooks",
      description: "Receive real-time notifications when data changes in Verdea. Use webhooks to trigger automated workflows in your systems."
    },
    {
      icon: Globe,
      title: "EU Data Residency",
      description: "Our API infrastructure is hosted in EU-based data centers, ensuring compliance with GDPR and other European data privacy regulations."
    },
    {
      icon: BarChart3,
      title: "Rate Limiting",
      description: "Protect our API infrastructure with rate limiting. We offer flexible rate limits to accommodate different use cases."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>API & Integrations | Verdea</title>
        <meta name="description" content="Integrate Verdea with your existing systems using our RESTful API. Automate data import, report generation, and more." />
      </Helmet>
      
      <NavBar />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-50 to-white pt-24 pb-16">
        <div className="absolute inset-0 z-0 opacity-40">
          <svg className="h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <pattern id="grid" width="8" height="8" patternUnits="userSpaceOnUse">
                <path d="M 8 0 L 0 0 0 8" fill="none" stroke="rgba(15, 61, 43, 0.05)" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-br from-[#1A1F2C] to-[#0F3D2B] bg-clip-text text-transparent">
              API & Integrations
            </h1>
            <p className="text-xl text-slate-700 mb-8 leading-relaxed max-w-3xl mx-auto">
              Integrate Verdea with your existing systems using our RESTful API. Automate data import, report generation, and more.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/documentation/api">
                <Button size="lg" className="bg-emerald-700 hover:bg-emerald-800 text-white">
                  API Documentation
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-emerald-700 text-emerald-700 hover:bg-emerald-50">
                  Contact Sales
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* API Features Section */}
      <section ref={featuresRef} className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-slate-900">
              Key API Features
            </h2>
            <p className="text-lg text-slate-600">
              Our API is designed to be easy to use and integrate with your existing systems
            </p>
          </motion.div>

          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate={controls}
            className="grid md:grid-cols-3 gap-8"
          >
            {apiFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow border border-slate-200"
                >
                  <div className="mb-4"><Icon className="h-10 w-10 text-emerald-600" /></div>
                  <h3 className="text-xl font-semibold mb-3 text-slate-900">{feature.title}</h3>
                  <p className="text-slate-600">{feature.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Code Example Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto"
          >
            <div className="bg-white rounded-xl overflow-hidden shadow-lg border border-slate-200">
              <div className="flex items-center justify-between bg-slate-100 px-6 py-3 border-b border-slate-200">
                <div className="flex items-center">
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  </div>
                  <span className="ml-4 text-sm font-medium text-slate-600">Example API Request</span>
                </div>
                <button className="text-slate-500 hover:text-slate-700">
                  <Copy className="h-4 w-4" />
                </button>
              </div>
              <div className="bg-slate-900 p-6 font-mono text-sm text-emerald-300 overflow-auto">
                <div className="text-slate-500">// Fetch a list of reports using the Verdea API</div>
                <div className="mt-2 text-emerald-400">fetch</div>
                <div>{'('}</div>
                <div className="ml-4">'https://api.verdea.com/api/v1/reports',</div>
                <div className="ml-4">{'{'}</div>
                <div className="ml-8">method: <span className="text-amber-300">'GET'</span>,</div>
                <div className="ml-8">headers: {'{'}</div>
                <div className="ml-12">
                  <span className="text-amber-300">'Authorization'</span>: <span className="text-amber-300">'Bearer YOUR_API_KEY'</span>,
                </div>
                <div className="ml-12">
                  <span className="text-amber-300">'Content-Type'</span>: <span className="text-amber-300">'application/json'</span>
                </div>
                <div className="ml-8">{'}'}</div>
                <div className="ml-4">{'}'}</div>
                <div>{')'}</div>
                <div className="ml-2">.then(response {'=>'} response.json())</div>
                <div className="ml-2">.then(data {'=>'} console.log(data))</div>
                <div className="ml-2">.catch(error {'=>'} console.error(error));</div>
              </div>
              <div className="bg-white px-6 py-4">
                <Link to="/documentation/api" className="text-emerald-700 font-medium hover:text-emerald-800 flex items-center">
                  View complete API documentation
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-20 bg-emerald-700 text-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Integrate Verdea?
            </h2>
            <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
              Contact our sales team to learn more about our API and integration options.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/documentation/api">
                <Button size="lg" className="bg-white text-emerald-700 hover:bg-emerald-50 min-w-[180px]">
                  API Documentation
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-emerald-600 min-w-[180px]">
                  Contact Sales
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default APIPage;
